/**
 * Query Key Factory — Centralized TanStack Query Key Management
 *
 * Go equivalent: Structured cache keys (prevents key collision / typo drift)
 *
 * All queryKey arrays should be constructed through this factory.
 * Benefits:
 * - Single source of truth for cache key structure
 * - Invalidation helpers (invalidate all estate queries, etc.)
 * - Compile-time safety via typed parameters
 * - Prevents string typo drift across components
 *
 * Pattern follows TanStack Query's recommended "query key factory" approach:
 * @see https://tanstack.com/query/latest/docs/framework/react/community/lukemorales-query-key-factory
 *
 * @example
 * ```ts
 * import { queryKeys } from '@/shared/lib/query-keys';
 *
 * // In a component:
 * useQuery({ queryKey: queryKeys.estates.suggest(query), ... });
 *
 * // Invalidation:
 * queryClient.invalidateQueries({ queryKey: queryKeys.estates.all });
 * ```
 */

import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import type { ListingId } from '@letsgethome/types/branded';

// =============================================================================
// Query Key Factory
// =============================================================================

export const queryKeys = {
  // ---------------------------------------------------------------------------
  // Estates
  // ---------------------------------------------------------------------------
  estates: {
    /** Root key for all estate-related queries */
    all: ['estates'] as const,
    /** Estate name suggestions (autocomplete) */
    suggest: (query: string) => ['estate-suggest', query] as const,
  },

  // ---------------------------------------------------------------------------
  // Payments (ADR 0151 — the wizard publication-fee step)
  // ---------------------------------------------------------------------------
  payments: {
    /** Root key for all payment queries */
    all: ['payments'] as const,
    /** The listing's webhook-written payment status */
    status: (listingId: ListingId) => ['payments', 'status', listingId] as const,
  },

  // ---------------------------------------------------------------------------
  // Listings
  // ---------------------------------------------------------------------------
  listings: {
    /** Root key for all listing-related queries */
    all: ['listings'] as const,
    /** Map view listings */
    map: (mode: TransactionModeValue, bounds: unknown, filters: unknown) =>
      ['map-listings', mode, bounds, filters] as const,
  },

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------
  search: {
    /** Root key for all search-related queries */
    all: ['search'] as const,
    /** Search autocomplete suggestions */
    suggestions: (query: string, limit: number) => ['search-suggestions', query, limit] as const,
  },

  // ---------------------------------------------------------------------------
  // Conversations
  // ---------------------------------------------------------------------------
  conversations: {
    /** Root key for all conversation-related queries */
    all: ['conversations'] as const,
    /** The Messages tab-badge unread-conversations count (R5: its own query). */
    unreadCount: ['conversations-unread-count'] as const,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    /** Root key for all filter-related queries */
    all: ['filters'] as const,
    /** Filters metadata (histogram, counts) */
    metadata: (...args: unknown[]) => ['filters-metadata', ...args] as const,
  },
} as const;
